import { differenceInCalendarDays } from 'date-fns';
import { FC, useCallback, useMemo } from 'react';
import { trackEvent } from 'src/common/3rdParty/tracking';
import DatesInputFilter, { DatesInputFilterProps } from 'src/common/components/DatesInputFilter';
import { GA_EVENT_FILTER } from 'src/common/gaEvents';
import useDateFilterRange from 'src/common/hooks/useDateFilterRange';

const dateRanges = [0, 1, 2, 3, 7];
const GA_PARAM_TRIP_DATE = 'p3';
const GA_PARAM_DATE_FLEXIBILITY = 'p4';

type FilterModalDatePickerProps = Pick<DatesInputFilterProps, 'dates' | 'onChange'>;

const FilterModalDatePicker: FC<FilterModalDatePickerProps> = ({ dates, onChange }) => {
  const { rangeOption, setRangeOption } = useDateFilterRange(dates, dateRanges);

  const dateRangeOptions = useMemo(
    () => dateRanges.map((option) => {
      const labelI18nKey = option === 0
        ? 'filter-range-buttons--exact-date'
        : `filter-range-buttons--option-${option === 1 ? 'single' : 'plural'}`;

      return {
        value: option,
        labelI18nKey,
        onRangeClick: (rangeDates: Date[] | []) => {
          onChange(rangeDates);
          setRangeOption(option);
          trackEvent(GA_EVENT_FILTER, {
            [GA_PARAM_TRIP_DATE]: differenceInCalendarDays(rangeDates[0], new Date()),
            ...(option > 0 && { [GA_PARAM_DATE_FLEXIBILITY]: option }),
          });
        },
      };
    }),
    [onChange, setRangeOption],
  );

  const handleDateFilterChange = useCallback(
    (dateRange: Date[]) => {
      onChange(dateRange);
      if (!dateRange.length) {
        setRangeOption(-1);
      }
      if (rangeOption < 0) {
        setRangeOption(0);
      }
      trackEvent(GA_EVENT_FILTER, {
        [GA_PARAM_TRIP_DATE]: differenceInCalendarDays(dateRange[0], new Date()),
        ...(rangeOption && { [GA_PARAM_DATE_FLEXIBILITY]: rangeOption }),
      });
    },
    [onChange, rangeOption, setRangeOption],
  );

  const highlightDates = [
    {
      'react-datepicker__day--highlighted--search-when-input': dates,
    },
  ];

  return (
    <DatesInputFilter
      dates={dates}
      dateRangeOptions={dateRangeOptions}
      rangeOption={rangeOption}
      classNameModifier="searchFilter"
      onChange={handleDateFilterChange}
      highlightDates={highlightDates}
      collapseLastRowIfEmpty
    />
  );
};

export default FilterModalDatePicker;
